import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ICallFlowStep } from '@dataTypes/clinic.type'
import StepPanelContainer from './StepPanelContainer.component'
import './PlayAudio/PlayAudioPanelStyle.css'
import {
  ActionButton,
  Button,
  Checkbox,
  InputField,
  Select,
  AudioPlayer,
} from '@enamelai/design-system'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'
import {
  REROUTE_CALL_YES_NO_OPTIONS,
  VOICEMAIL_AFTER_RECORDING_OPTIONS,
} from '@constants/Settings'

export type VoicemailPanelProps = {
  step: Extract<ICallFlowStep, { type: 'voicemail' }>
  branchLabel: string
  onChange: (
    patch: Partial<Extract<ICallFlowStep, { type: 'voicemail' }>>,
  ) => void
  onCancel: () => void
  onDone: () => void
  onPendingFile?: (file: File | null) => void
}

const AUDIO_ACCEPT = '.mp3,.wav,audio/mpeg,audio/wav,audio/*'
const MAX_BYTES = 50 * 1024 * 1024
const INVALID_AUDIO_MSG = 'Invalid file. Please upload a valid audio file'
const INVALID_SIZE_MSG = 'File is too large. Maximum size is 50 MB.'

const AUDIO_EXT_RE = /\.(mp3|wav|m4a|aac|ogg|flac|webm|opus)$/i

const isAudioMimeOrExtension = (file: File): boolean => {
  if (file.type && file.type.startsWith('audio/')) return true
  return AUDIO_EXT_RE.test(file.name)
}

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type LocalPreview = { url: string; file: File }

const VoicemailPanel = ({
  step,
  branchLabel,
  onChange,
  onCancel,
  onDone,
  onPendingFile,
}: VoicemailPanelProps): React.ReactElement => {
  const fileRef = useRef<HTMLInputElement>(null)
  const previewRef = useRef<LocalPreview | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const [localPreview, setLocalPreview] = useState<LocalPreview | null>(null)
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  const clearValidation = useCallback(() => {
    setValidationErrors({})
  }, [])

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (
      step.voicemailAllowRerecord === true &&
      step.includeVoicemailTranscription === undefined
    ) {
      onChangeRef.current({ includeVoicemailTranscription: false })
    }
  }, [step.voicemailAllowRerecord, step.includeVoicemailTranscription])

  const hasUploadedFile = Boolean(step.greetingFileName?.trim())
  const hasRemoteUrlOnly = Boolean(
    step.greetingAudioUrl?.trim() && !hasUploadedFile,
  )
  const showDropZone = !hasUploadedFile && !hasRemoteUrlOnly

  const replacePreview = (next: LocalPreview | null): void => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current.url)
    }
    previewRef.current = next
    setLocalPreview(next)
  }

  useEffect(() => {
    if (!step.greetingFileName?.trim()) {
      replacePreview(null)
    }
  }, [step.greetingFileName])

  useEffect(() => {
    return () => {
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current.url)
        previewRef.current = null
      }
    }
  }, [])

  const applyFile = useCallback(
    (file: File | undefined) => {
      if (!file) return
      if (!isAudioMimeOrExtension(file)) {
        setFileError(INVALID_AUDIO_MSG)
        return
      }
      if (file.size > MAX_BYTES) {
        setFileError(INVALID_SIZE_MSG)
        return
      }
      setFileError(null)
      onPendingFile?.(file)
      replacePreview({ url: URL.createObjectURL(file), file })
      setValidationErrors({})
      onChange({
        greetingFileName: file.name,
        greetingFileSizeBytes: file.size,
      })
    },
    [onChange, onPendingFile],
  )

  const openPicker = useCallback(() => {
    fileRef.current?.click()
  }, [])

  const clearFile = useCallback(() => {
    setFileError(null)
    setValidationErrors({})
    onPendingFile?.(null)
    replacePreview(null)
    onChange({
      greetingFileName: undefined,
      greetingFileSizeBytes: undefined,
    })
    if (fileRef.current) {
      fileRef.current.value = ''
    }
  }, [onChange, onPendingFile])

  const playbackSrc =
    localPreview?.url ??
    (step.greetingAudioUrl?.trim() ? step.greetingAudioUrl.trim() : '')

  const displaySizeBytes =
    step.greetingFileSizeBytes ?? localPreview?.file.size ?? 0

  const rerecordValue =
    step.voicemailAllowRerecord == null
      ? null
      : step.voicemailAllowRerecord
        ? 'yes'
        : 'no'

  const rerecordYes = step.voicemailAllowRerecord === true

  const hasGreetingAudio = Boolean(
    step.greetingFileName?.trim() || step.greetingAudioUrl?.trim(),
  )

  const handleDone = (): void => {
    const errs: Record<string, string> = {}
    if (!hasGreetingAudio) {
      errs.greetingAudio = 'Upload a valid audio file.'
    }
    if (
      step.maxRecordingSec == null ||
      Number.isNaN(step.maxRecordingSec) ||
      step.maxRecordingSec <= 0
    ) {
      errs.maxRecordingSec = 'Invalid seconds.'
    }
    if (step.voicemailAllowRerecord == null) {
      errs.voicemailAllowRerecord = 'Select yes or no.'
    }
    if (step.voicemailAllowRerecord === true) {
      if (step.includeVoicemailTranscription == null) {
        errs.includeVoicemailTranscription =
          'Select whether to include transcription.'
      }
    }
    if (!step.afterRecording?.trim()) {
      errs.afterRecording = 'Select an action.'
    }
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs)
      return
    }
    setValidationErrors({})
    onDone()
  }

  return (
    <StepPanelContainer
      title="Voicemail"
      branchLabel={branchLabel}
      confirmLabel="Save"
      onCancel={onCancel}
      onDone={handleDone}
    >
      <div className="scp-panel-field scp-play-audio-field">
        <span className="scp-panel-label">Upload audio file</span>
        {fileError && <span className="hint-text">{fileError}</span>}
        {validationErrors.greetingAudio && (
          <span className="hint-text" role="alert">
            {validationErrors.greetingAudio}
          </span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={AUDIO_ACCEPT}
          className="scp-step-file-input-hidden"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            applyFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />

        {!showDropZone && !hasUploadedFile && hasRemoteUrlOnly ? (
          <>
            <div className="scp-play-audio-file-card scp-voicemail-remote-audio">
              <i className="mgc-icon mgc_file_music_line" />
              <div className="scp-play-audio-file-card-meta">
                <span className="scp-play-audio-file-card-name">
                  Remote audio URL
                </span>
                <span className="scp-play-audio-file-card-size">
                  {step.greetingAudioUrl}
                </span>
              </div>
              <ActionButton
                size="sm"
                tone="danger"
                variant="outline"
                label="Remove audio"
                icon={
                  <CloseIcon
                    className="ds-action-button__svg"
                    aria-hidden="true"
                  />
                }
                onClick={() => {
                  setValidationErrors({})
                  onChange({
                    greetingAudioUrl: undefined,
                  })
                }}
              />
            </div>
            {playbackSrc ? <AudioPlayer src={playbackSrc} /> : null}
          </>
        ) : null}

        {showDropZone ? (
          <div
            className={`scp-play-audio-upload-box${dragOver ? ' scp-play-audio-drop-zone' : ''}${validationErrors.greetingAudio ? ' scp-destructive-box' : ''}`}
            role="button"
            tabIndex={0}
            aria-label="Upload audio file"
            onClick={() => {
              setValidationErrors({})
              openPicker()
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return
              e.preventDefault()
              setValidationErrors({})
              openPicker()
            }}
            onDragEnter={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragOver(true)
            }}
            onDragOver={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragOver(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setDragOver(false)
              }
            }}
            onDrop={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setDragOver(false)
              setValidationErrors({})
              applyFile(e.dataTransfer.files?.[0])
            }}
          >
            <div className="scp-play-audio-upload-main">
              <i className="upload-icon mgc_play_circle_fill" />
              <span>
                Drop your audio file here, or
                <Button
                  type="button"
                  size="md"
                  variant="ghost"
                  className="button-link"
                  onClick={(e) => {
                    e.stopPropagation()
                    setValidationErrors({})
                    openPicker()
                  }}
                >
                  click here
                </Button>
                to upload
              </span>
              <span className="scp-play-audio-sub-title">
                MP3, WAV (50 MB max size)
              </span>
            </div>
          </div>
        ) : hasUploadedFile ? (
          <>
            <div className="scp-play-audio-file-card">
              <i className="mgc-icon mgc_file_music_line" />
              <div className="scp-play-audio-file-card-meta">
                <span className="scp-play-audio-file-card-name">
                  {step.greetingFileName}
                </span>
                <span className="scp-play-audio-file-card-size">
                  {displaySizeBytes > 0
                    ? formatFileSize(displaySizeBytes)
                    : '—'}
                </span>
              </div>
              <ActionButton
                size="sm"
                tone="danger"
                variant="outline"
                label="Remove audio"
                icon={
                  <CloseIcon
                    className="ds-action-button__svg"
                    aria-hidden="true"
                  />
                }
                onClick={clearFile}
              />
            </div>
            {playbackSrc ? <AudioPlayer src={playbackSrc} /> : null}
          </>
        ) : null}
      </div>

      <div className="scp-row">
        <div className="scp-panel-field">
          <Select
            label="Allow caller to re-record"
            placeholder="Select"
            options={REROUTE_CALL_YES_NO_OPTIONS}
            value={rerecordValue || undefined}
            error={!!validationErrors.voicemailAllowRerecord}
            helperText={validationErrors.voicemailAllowRerecord}
            onChange={(value) => {
              clearValidation()
              const next = String(value || '')
              if (next === 'yes') {
                onChange({
                  voicemailAllowRerecord: true,
                  includeVoicemailTranscription:
                    step.includeVoicemailTranscription ?? false,
                })
              } else if (next === 'no') {
                onChange({
                  voicemailAllowRerecord: false,
                  includeVoicemailTranscription: undefined,
                })
              } else {
                onChange({
                  voicemailAllowRerecord: undefined,
                  includeVoicemailTranscription: undefined,
                })
              }
            }}
          />
        </div>
        <div className="scp-panel-field scp-recording-length">
          <InputField
            variant="number"
            fullWidth
            label="Max recording length"
            numberMin={1}
            placeholder="160"
            clearable={false}
            trailingIcon={<span>sec</span>}
            value={
              step.maxRecordingSec != null &&
              !Number.isNaN(step.maxRecordingSec)
                ? String(step.maxRecordingSec)
                : ''
            }
            onChange={(event) => {
              clearValidation()
              const raw = event.target.value
              onChange({
                maxRecordingSec:
                  raw === '' ? undefined : Math.floor(Number(raw)),
              })
            }}
            error={!!validationErrors.maxRecordingSec}
            helperText={validationErrors.maxRecordingSec}
          />
        </div>
      </div>

      <div className="scp-panel-field">
        <div
          className={`scp-switch-label${!rerecordYes ? ' scp-switch-label--disabled' : ''}`}
        >
          <Checkbox
            label="Include transcription"
            checked={!!step.includeVoicemailTranscription}
            disabled={!rerecordYes}
            onChange={(event) => {
              clearValidation()
              onChange({
                includeVoicemailTranscription: event.target.checked,
              })
            }}
          />
        </div>
        {validationErrors.includeVoicemailTranscription && (
          <span className="hint-text" role="alert">
            {validationErrors.includeVoicemailTranscription}
          </span>
        )}
      </div>

      <div className="scp-panel-field">
        <Select
          label="After recording"
          placeholder="Select action"
          options={VOICEMAIL_AFTER_RECORDING_OPTIONS}
          value={step.afterRecording || undefined}
          error={!!validationErrors.afterRecording}
          helperText={validationErrors.afterRecording}
          onChange={(value) => {
            clearValidation()
            const next = String(value || '')
            onChange({ afterRecording: next || undefined })
          }}
        />
      </div>
    </StepPanelContainer>
  )
}

export default VoicemailPanel
